/**
 * Auth owns the whole viewport — no sidebar, no tab bar. One field group per
 * screen, plain copy, no marketing (DESIGN.md).
 */
import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect } from 'react'
import { BrandLockup } from '@/components/app/brand-lockup'
import { brand, pageTitle } from '@/lib/brand'

export function AuthShell({
  title,
  documentTitle = title,
  description,
  children,
  footer,
}: {
  title: string
  /** The tab's page name, when it isn't the heading (a heading that names an app, say). */
  documentTitle?: string
  description?: string
  children: ReactNode
  footer?: ReactNode
}) {
  // "{Page} · {App}" here too — signed-out tabs would otherwise keep
  // index.html's title.
  useEffect(() => {
    document.title = pageTitle(documentTitle)
  }, [documentTitle])

  return (
    // Full height, less any bar pinned above it (the Sandbox strip).
    <div className="flex min-h-[calc(100dvh-var(--top-banner))] flex-col items-center justify-center gap-8 bg-background px-4 py-12">
      <Link to="/" className="flex items-center gap-3" aria-label={brand.name}>
        <BrandLockup tone="surface" size="md" />
      </Link>

      <div className="w-full max-w-[400px] rounded-lg border bg-card p-6 shadow-raised">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-xl font-semibold tracking-[-0.01em]">{title}</h1>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        <div className="mt-6 flex flex-col gap-4">{children}</div>
      </div>

      {footer ? <p className="text-sm text-muted-foreground">{footer}</p> : null}
    </div>
  )
}

/** Errors say what went wrong; they never blame the person reading them. */
export function AuthError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p
      role="alert"
      className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive-strong"
    >
      {message}
    </p>
  )
}

export function AuthNotice({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success-strong">
      {message}
    </p>
  )
}
